import {tokens, colors} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import {useEmailValidationErrorMessage} from '@/hooks/use-email-validation-error-message'
import useSignup from '@/hooks/use-signup'
import {CheckmarkList} from '@/styles/lists'
import hasCookieExperiment from '@/utils/has-cookie-experiment'
import Button from '@helios/shared/components/button'
import Checkbox from '@helios/shared/components/checkbox'
import {GlobalText} from '@helios/shared/components/global-text'
import Input from '@helios/shared/components/input'
import type {Props as ModalProps} from '@helios/shared/components/modal'
import {Modal, ModalContent, ModalActions} from '@helios/shared/components/modal'
import checkIconPink from '@helios/shared/images/icons/check-pink.svg'
import {useGlobalStrings} from '@helios/shared/providers/global-strings'
import {text} from '@helios/shared/styles/typography'
import {track, TrackingEvent} from '@helios/shared/utils/analytics'

interface Props extends Omit<ModalProps, 'children'> {
    signupProps?: any
    formId: string
    highlightedFeatures: string[]
}

export const SignupModal = ({
    isOpen,
    onDismiss,
    title,
    subtitle,
    image,
    formId,
    signupProps,
    highlightedFeatures,
}: Props) => {
    const globalStrings = useGlobalStrings()
    const {handleSubmit, clearError, setEmail, email, loading, error} = useSignup(
        {
            form: 'Front',
            params: signupProps,
        },
        formId
    )
    const errorMessage = useEmailValidationErrorMessage(error)

    // Marketing emails consent A/B test
    const [approvedMarketingEmails, setApprovedMarketingEmails] = React.useState(false)
    const showMarketingConsent = hasCookieExperiment({
        experimentName: 'marketing-emails-consent',
        bucketName: 'on',
    })

    const handleEmailChange = (value: string) => {
        clearError()
        setEmail(value)
    }

    const handleDismiss = () => {
        clearError()
        setEmail('')
        onDismiss && onDismiss()
    }

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        if (showMarketingConsent) {
            track(TrackingEvent.SignupFormSubmitted, {consented: approvedMarketingEmails})
        }

        handleSubmit(event)
    }

    const hasHighlightedFeatures = highlightedFeatures && highlightedFeatures.length > 0

    React.useEffect(() => {
        if (isOpen) {
            track(TrackingEvent.SignupFormModalOpened, {action: 'opened'})
        }
    }, [isOpen])

    return (
        <Modal
            title={title}
            subtitle={subtitle}
            image={image}
            isOpen={isOpen}
            onDismiss={handleDismiss}
        >
            <form onSubmit={onSubmit} noValidate>
                <ModalContent>
                    {hasHighlightedFeatures && (
                        <FeaturesList>
                            {highlightedFeatures.map((feature, idx) => (
                                <Item key={idx}>{feature}</Item>
                            ))}
                        </FeaturesList>
                    )}
                    <Input
                        type="email"
                        label={globalStrings.work_email}
                        value={email}
                        onChange={handleEmailChange}
                        error={errorMessage}
                        autoFocus
                        data-testid="modal-input"
                    />
                </ModalContent>

                {showMarketingConsent && (
                    <ConsentWrapper>
                        <Checkbox
                            name="marketingConsent"
                            label={globalStrings.marketingEmailsConsentText}
                            onChange={setApprovedMarketingEmails}
                            checked={approvedMarketingEmails}
                        />
                    </ConsentWrapper>
                )}

                <ModalActions>
                    <Button type="submit" loading={loading} data-testid="modal-submit-button">
                        {globalStrings.submit}
                    </Button>
                </ModalActions>
                <StyledGlobalText
                    globalStringKey="financial_partner_disclaimer"
                    variant="label-base"
                />
            </form>
        </Modal>
    )
}

const FeaturesList = styled(CheckmarkList)`
    margin: 0;
    gap: ${tokens.spacing4};
`

const Item = styled.li`
    ${text({variant: 'paragraph-base'})}
    color: ${colors.text.muted};

    ::before {
        padding-right: ${tokens.spacing12};
        content: url(${checkIconPink});
    }
`

const ConsentWrapper = styled.div`
    margin-top: ${tokens.spacing20};
    margin-bottom: ${tokens.spacing32};
`

const StyledGlobalText = styled(GlobalText)`
    margin-top: ${tokens.spacing12};
`
